import { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ApiError, api } from './lib/api.ts';
import type { User } from './lib/types.ts';
import { AuthProvider, useAuth } from './contexts/auth-context.tsx';
import { ThemeProvider } from './contexts/theme-context.tsx';
import { AppShell } from './components/layout/AppShell.tsx';
import { LoginPage } from './routes/login/LoginPage.tsx';
import { OverviewPage } from './routes/overview/OverviewPage.tsx';
import { ProductPage } from './routes/product/ProductPage.tsx';
import { TheoryPage } from './routes/theory/TheoryPage.tsx';
import { SettingsPage } from './routes/settings/SettingsPage.tsx';
import { UsersPage } from './routes/users/UsersPage.tsx';

// Query client с консервативными дефолтами: ошибки не глушим
// автоматически, retry только на сетевые сбои (не на 4xx).
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, error) => {
        if (error instanceof ApiError && error.status < 500) return false;
        return count < 2;
      },
      staleTime: 10_000,
      refetchOnWindowFocus: false,
    },
  },
});

export function App(): JSX.Element {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AuthProbe />
          <BrowserRouter>
            <Router />
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

/** На старте проверяем сессию через GET /auth/me. Если 401 —
 *  отправляем юзера в anonymous; если 200 — кладём user в state. */
function AuthProbe(): null {
  const { setUser, state } = useAuth();
  useEffect(() => {
    if (state.kind !== 'loading') return;
    void (async () => {
      try {
        const { user } = await api.get<{ user: User }>('/auth/me');
        setUser(user);
      } catch {
        setUser(null);
      }
    })();
  }, [state.kind, setUser]);
  return null;
}

function Router(): JSX.Element {
  const { state } = useAuth();

  if (state.kind === 'loading') {
    return <div className="flex min-h-screen items-center justify-center text-fg-muted">…</div>;
  }

  if (state.kind !== 'authenticated') {
    return (
      <Routes>
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route path="/" element={<OverviewPage />} />
        <Route path="/products/:id" element={<ProductPage />} />
        <Route path="/theories/:id" element={<TheoryPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/users" element={<UsersPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
